import { NavLink } from "react-router-dom";
import { isNotesOnly, navItems, navGroups } from "./navItems";
import { useAuth } from "@/contexts/AuthContext";

interface SidebarProps {
  mobileOpen: boolean;
  onClose: () => void;
}

export default function Sidebar({ mobileOpen, onClose }: SidebarProps) {
  const { can, activeSchool, isPlatformAdmin } = useAuth();

  return (
    <>
      {/* Voile du tiroir (téléphone) : il apparaît et disparaît en fondu au lieu de surgir. */}
      <div
        className={`fixed inset-0 z-40 bg-foreground-950/40 transition-opacity duration-300 ease-out lg:hidden ${
          mobileOpen ? "opacity-100" : "pointer-events-none opacity-0"
        }`}
        onClick={onClose}
        aria-hidden="true"
      ></div>
      <aside
        aria-label="Menu principal"
        className={`fixed inset-y-0 left-0 z-50 flex w-[272px] max-w-[85vw] flex-col border-r border-background-200 bg-background-50 pb-[env(safe-area-inset-bottom,0px)] pt-[env(safe-area-inset-top,0px)] transition-transform duration-300 ease-drawer lg:w-[260px] lg:translate-x-0 lg:shadow-none ${
          mobileOpen ? "translate-x-0 shadow-modal" : "-translate-x-full"
        }`}
      >
        <div className="flex h-16 shrink-0 items-center gap-3 border-b border-background-200 px-5">
          <span className="w-9 h-9 shrink-0 overflow-hidden rounded-md bg-primary-600 text-white shadow-card flex items-center justify-center">
            {activeSchool?.logo ? (
              <img
                src={activeSchool.logo}
                alt={activeSchool.name}
                title={activeSchool.name}
                className="h-full w-full object-cover"
              />
            ) : (
              <img src="/logo-mark.png" alt="WaitechEdu" className="h-full w-full bg-white object-contain" />
            )}
          </span>
          <div className="leading-tight min-w-0">
            <p className="truncate text-sm font-semibold font-heading text-foreground-950">
              {activeSchool?.shortName ?? "WaitechEdu"}
            </p>
            <p className="truncate text-xs text-foreground-500">
              {isPlatformAdmin ? "Console plateforme" : "Gestion scolaire"}
            </p>
          </div>
        </div>

        <nav className="min-h-0 flex-1 overflow-y-auto overscroll-contain px-3 py-4">
          {navGroups.map((group) => {
            const items = navItems.filter(
              (i) =>
                i.group === group &&
                Boolean(i.platform) === isPlatformAdmin &&
                !(i.to === "/dashboard" && isNotesOnly(can)) &&
                (i.anyOf ? i.anyOf.some((p) => can(p)) : !i.permission || can(i.permission)),
            );
            if (items.length === 0) return null;
            return (
              <div key={group} className="mb-5">
                <p className="px-3 mb-1.5 text-xs font-semibold uppercase tracking-wide text-foreground-400">
                  {group}
                </p>
                <div className="space-y-0.5">
                  {items.map((item) => (
                    <NavLink
                      key={item.to}
                      to={item.to}
                      end={item.to === "/dashboard"}
                      onClick={onClose}
                      className={({ isActive }) =>
                        `relative flex min-h-[2.75rem] items-center gap-3 rounded-md px-3 text-sm transition-colors duration-150 cursor-pointer active:bg-background-200 lg:min-h-[2.25rem] ${
                          isActive
                            ? "bg-primary-50 text-primary-800 font-semibold before:absolute before:inset-y-2 before:left-0 before:w-[3px] before:rounded-full before:bg-primary-600"
                            : "text-foreground-600 hover:bg-background-100 hover:text-foreground-900"
                        }`
                      }
                    >
                      <span className="w-5 h-5 flex items-center justify-center shrink-0" aria-hidden="true">
                        <i className={`${item.icon} text-lg leading-none`}></i>
                      </span>
                      <span className="truncate">{item.label}</span>
                    </NavLink>
                  ))}
                </div>
              </div>
            );
          })}
        </nav>
      </aside>
    </>
  );
}